import { useEffect, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { PageHeader, Loading } from "@/components/Layout";
import { Modal, ModalField, ModalSelect } from "@/pages/Channels";
import { Buildings, Plus, ShieldCheck, Television, Users, CheckCircle, Prohibit, PencilSimple, Trash, UserPlus, Key, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_CLS = {
  owner: "text-[#3A86FF] bg-[#3A86FF]/10 border-[#3A86FF]/20",
  admin: "text-amber-500 bg-amber-500/10 border-amber-500/20",
  operator: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20",
  viewer: "text-neutral-400 bg-neutral-500/10 border-neutral-500/20",
};
const FEATURES = [
  { key: "ccs_core", label: "CCS Core" },
  { key: "scte", label: "SCTE" },
  { key: "cgle", label: "CGLE" },
  { key: "advanced_programming_enabled", label: "Scheduled Programming" },
  { key: "epg_enabled", label: "EPG / XMLTV" },
];

export default function PlatformAdmin() {
  const [orgs, setOrgs] = useState(null);
  const [selected, setSelected] = useState(null); // org_id for detail view
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => api.get("/platform/organizations").then((r) => setOrgs(r.data)), []);
  useEffect(() => { load(); }, [load]);

  if (selected) return <OrgDetail orgId={selected} onBack={() => { setSelected(null); load(); }} />;

  return (
    <>
      <PageHeader title="Platform Administration" subtitle="ReVine — customer organizations & entitlements"
        actions={
          <button data-testid="create-org-btn" onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white text-sm font-medium px-4 py-2 rounded-sm">
            <Plus size={16} weight="bold" /> New Organization
          </button>
        } />

      <div className="p-8 ccs-enter" data-testid="platform-admin-page">
        {!orgs ? <Loading /> : orgs.length === 0 ? (
          <Empty />
        ) : (
          <div className="border border-[#262626] rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#141414] text-[#737373] text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left font-bold px-4 py-3">Organization</th>
                  <th className="text-left font-bold px-4 py-3">Status</th>
                  <th className="text-left font-bold px-4 py-3">Channels</th>
                  <th className="text-left font-bold px-4 py-3">Features</th>
                  <th className="text-left font-bold px-4 py-3">Users</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {orgs.map((o) => (
                  <tr key={o.id} data-testid={`org-row-${o.id}`} className="border-t border-[#262626] hover:bg-[#141414] transition-colors duration-150">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 font-medium">
                        {o.is_internal ? <ShieldCheck size={16} className="text-[#3A86FF]" /> : <Buildings size={16} className="text-[#737373]" />}
                        {o.name}
                        {o.is_internal && <span className="text-[9px] uppercase tracking-wider text-[#3A86FF] border border-[#3A86FF]/30 rounded px-1.5 py-0.5">Platform</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3"><StatusPill status={o.status} /></td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {o.channel_usage.used}{o.channel_usage.max == null ? " / ∞" : ` / ${o.channel_usage.max}`}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {FEATURES.map((f) => (
                          <span key={f.key} className={`text-[9px] uppercase tracking-wider rounded px-1.5 py-0.5 border ${o.features?.[f.key] ? "text-emerald-500 border-emerald-500/30 bg-emerald-500/10" : "text-[#525252] border-[#262626]"}`}>{f.label}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{o.user_count}</td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`manage-org-${o.id}`} onClick={() => setSelected(o.id)}
                        className="text-xs text-[#3A86FF] hover:underline">Manage</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && <CreateOrgModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </>
  );
}

function Empty() {
  return (
    <div className="text-center py-24 text-[#737373]" data-testid="platform-empty">
      <Buildings size={40} className="mx-auto mb-3 opacity-50" />
      <p className="text-sm">No customer organizations yet. Create your first organization to onboard a customer.</p>
    </div>
  );
}

function StatusPill({ status }) {
  const active = status === "active";
  return (
    <span data-testid="org-status-pill" className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider rounded px-2 py-0.5 border ${active ? "text-emerald-500 border-emerald-500/30 bg-emerald-500/10" : "text-red-500 border-red-500/30 bg-red-500/10"}`}>
      {active ? <CheckCircle size={11} /> : <Prohibit size={11} />}{status}
    </span>
  );
}

function CreateOrgModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", status: "active", max_channels: 3, ccs_core: true, scte: false, cgle: false, owner_name: "", owner_email: "", owner_password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (k) => () => setF({ ...f, [k]: !f[k] });

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/platform/organizations", {
        name: f.name, status: f.status, max_channels: Number(f.max_channels),
        features: { ccs_core: f.ccs_core, scte: f.scte, cgle: f.cgle },
        owner_name: f.owner_name, owner_email: f.owner_email, owner_password: f.owner_password,
      });
      toast.success("Organization provisioned");
      onCreated();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to create organization");
      setLoading(false);
    }
  };

  return (
    <Modal title="New Customer Organization" onClose={onClose} size="lg">
      <div className="space-y-4" data-testid="create-org-modal">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] font-bold">Organization</div>
        <ModalField label="Organization name" testid="org-name-input" value={f.name} onChange={set("name")} placeholder="e.g. ABC Ministries" />
        <div className="grid grid-cols-2 gap-3">
          <ModalSelect label="Status" testid="org-status-select" value={f.status} onChange={set("status")}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </ModalSelect>
          <ModalField label="Max channels" testid="org-maxchannels-input" type="number" min="1" value={f.max_channels} onChange={set("max_channels")} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-2 font-bold">Feature entitlements</div>
          <div className="flex gap-2">
            {FEATURES.map((ft) => (
              <button key={ft.key} type="button" data-testid={`feature-toggle-${ft.key}`} onClick={toggle(ft.key)}
                className={`text-xs rounded-sm px-3 py-2 border transition-colors duration-150 ${f[ft.key] ? "text-emerald-500 border-emerald-500/40 bg-emerald-500/10" : "text-[#737373] border-[#262626] hover:border-[#3A86FF]"}`}>
                {ft.label}: {f[ft.key] ? "ON" : "OFF"}
              </button>
            ))}
          </div>
        </div>
        <div className="border-t border-[#262626] pt-4 text-[10px] uppercase tracking-[0.2em] text-[#737373] font-bold">Initial Owner</div>
        <ModalField label="Owner name" testid="owner-name-input" value={f.owner_name} onChange={set("owner_name")} />
        <div className="grid grid-cols-2 gap-3">
          <ModalField label="Owner email" testid="owner-email-input" type="email" value={f.owner_email} onChange={set("owner_email")} />
          <ModalField label="Owner password" testid="owner-password-input" type="password" value={f.owner_password} onChange={set("owner_password")} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-sm text-[#A3A3A3] hover:text-white px-4 py-2">Cancel</button>
          <button data-testid="submit-create-org" disabled={loading} onClick={submit}
            className="bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white text-sm font-medium px-4 py-2 rounded-sm disabled:opacity-60">
            {loading ? "Creating…" : "Provision Organization"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function OrgDetail({ orgId, onBack }) {
  const [org, setOrg] = useState(null);
  const [modal, setModal] = useState(null); // {type, user}
  const [maxCh, setMaxCh] = useState(0);

  const load = useCallback(() => api.get(`/platform/organizations/${orgId}`).then((r) => {
    setOrg(r.data);
    setMaxCh(r.data.channel_usage.max ?? 0);
  }), [orgId]);
  useEffect(() => { load(); }, [load]);

  const patch = async (body, msg) => {
    try {
      await api.patch(`/platform/organizations/${orgId}`, body);
      toast.success(msg || "Updated");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  if (!org) return (<><PageHeader title="Organization" /><Loading /></>);

  return (
    <>
      <PageHeader title={org.name} subtitle="Organization administration"
        actions={
          <button data-testid="back-to-orgs" onClick={onBack} className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-white">
            <ArrowLeft size={16} /> All organizations
          </button>
        } />
      <div className="p-8 space-y-6 ccs-enter max-w-4xl" data-testid="org-detail-page">
        {/* Status + entitlements */}
        <div className="bg-[#141414] border border-[#262626] rounded-sm p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1 font-bold">Status</div>
              <StatusPill status={org.status} />
            </div>
            {!org.is_internal && (
              org.status === "active" ? (
                <button data-testid="deactivate-org-btn" onClick={() => patch({ status: "inactive" }, "Organization deactivated")}
                  className="flex items-center gap-2 text-xs text-red-500 border border-red-500/30 hover:bg-red-500/10 rounded-sm px-3 py-2 transition-colors duration-150">
                  <Prohibit size={14} /> Deactivate
                </button>
              ) : (
                <button data-testid="activate-org-btn" onClick={() => patch({ status: "active" }, "Organization activated")}
                  className="flex items-center gap-2 text-xs text-emerald-500 border border-emerald-500/30 hover:bg-emerald-500/10 rounded-sm px-3 py-2 transition-colors duration-150">
                  <CheckCircle size={14} /> Activate
                </button>
              )
            )}
          </div>

          <div className="grid grid-cols-2 gap-6 border-t border-[#262626] pt-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-2 font-bold flex items-center gap-1.5"><Television size={13} /> Channel entitlement</div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-lg" data-testid="org-usage">
                  {org.channel_usage.used}<span className="text-[#525252]">{org.channel_usage.max == null ? " / ∞" : ` / ${org.channel_usage.max}`}</span>
                </span>
                {org.channel_usage.available != null && <span className="text-xs text-[#737373]">{org.channel_usage.available} available</span>}
              </div>
              {!org.is_internal && (
                <div className="flex items-center gap-2 mt-3">
                  <input data-testid="max-channels-input" type="number" min="1" value={maxCh}
                    onChange={(e) => setMaxCh(e.target.value)}
                    className="w-24 bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-2 py-1.5 text-sm outline-none" />
                  <button data-testid="save-max-channels" onClick={() => patch({ max_channels: Number(maxCh) }, "Channel limit updated")}
                    className="text-xs bg-[#262626] hover:bg-[#333] rounded-sm px-3 py-1.5 transition-colors duration-150">Update limit</button>
                </div>
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-2 font-bold">Feature entitlements</div>
              <div className="flex flex-wrap gap-2">
                {FEATURES.map((ft) => (
                  <button key={ft.key} type="button" data-testid={`org-feature-${ft.key}`}
                    disabled={org.is_internal && ft.key !== "scte"}
                    onClick={() => patch({ features: { ...featureMap(org.features), [ft.key]: !org.features?.[ft.key] } }, `${ft.label} ${!org.features?.[ft.key] ? "enabled" : "disabled"}`)}
                    className={`text-xs rounded-sm px-3 py-2 border transition-colors duration-150 disabled:opacity-50 ${org.features?.[ft.key] ? "text-emerald-500 border-emerald-500/40 bg-emerald-500/10" : "text-[#737373] border-[#262626] hover:border-[#3A86FF]"}`}>
                    {ft.label}: {org.features?.[ft.key] ? "ON" : "OFF"}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Users */}
        <div className="bg-[#141414] border border-[#262626] rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-semibold text-sm flex items-center gap-2"><Users size={16} className="text-[#3A86FF]" /> Customer Users</h3>
            <button data-testid="add-user-btn" onClick={() => setModal({ type: "add" })}
              className="flex items-center gap-2 text-xs bg-[#3A86FF] hover:bg-[#2563EB] text-white rounded-sm px-3 py-2 transition-colors duration-150">
              <UserPlus size={14} /> Add User
            </button>
          </div>
          <div className="space-y-2">
            {org.users.map((u) => (
              <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center justify-between border border-[#262626] rounded-sm px-4 py-3">
                <div>
                  <div className="text-sm font-medium flex items-center gap-2">
                    {u.name}
                    {u.is_platform_admin && <span className="text-[9px] uppercase tracking-wider text-[#3A86FF] border border-[#3A86FF]/30 rounded px-1.5 py-0.5">Platform Admin</span>}
                    {u.disabled && <span className="text-[9px] uppercase tracking-wider text-red-500 border border-red-500/30 rounded px-1.5 py-0.5">Disabled</span>}
                  </div>
                  <div className="text-xs text-[#737373]">{u.email}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] uppercase tracking-wider rounded px-2 py-0.5 border ${ROLE_CLS[u.role]}`}>{u.role}</span>
                  <button data-testid={`edit-user-${u.id}`} onClick={() => setModal({ type: "edit", user: u })} className="text-[#737373] hover:text-white"><PencilSimple size={15} /></button>
                  <button data-testid={`reset-user-${u.id}`} onClick={() => setModal({ type: "reset", user: u })} className="text-[#737373] hover:text-amber-500"><Key size={15} /></button>
                  {!u.is_platform_admin && u.role !== "owner" && (
                    <button data-testid={`delete-user-${u.id}`} onClick={() => setModal({ type: "delete", user: u })} className="text-[#737373] hover:text-red-500"><Trash size={15} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {modal && <UserModal orgId={orgId} modal={modal} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
    </>
  );
}

function featureMap(features) {
  return { ccs_core: !!features?.ccs_core, scte: !!features?.scte, cgle: !!features?.cgle, advanced_programming_enabled: !!features?.advanced_programming_enabled, epg_enabled: !!features?.epg_enabled };
}

function UserModal({ orgId, modal, onClose, onDone }) {
  const { type, user } = modal;
  const [f, setF] = useState({ name: user?.name || "", email: "", password: "", role: user?.role || "operator" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const act = async () => {
    setLoading(true);
    try {
      if (type === "add") {
        await api.post(`/platform/organizations/${orgId}/users`, { name: f.name, email: f.email, password: f.password, role: f.role });
        toast.success("User created");
      } else if (type === "edit") {
        await api.patch(`/platform/organizations/${orgId}/users/${user.id}`, { name: f.name, role: f.role });
        toast.success("User updated");
      } else if (type === "reset") {
        await api.patch(`/platform/organizations/${orgId}/users/${user.id}`, { password: f.password });
        toast.success("Password reset");
      } else if (type === "delete") {
        await api.delete(`/platform/organizations/${orgId}/users/${user.id}`);
        toast.success("User removed");
      }
      onDone();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const titles = { add: "Add Customer User", edit: "Edit User", reset: "Reset Password", delete: "Remove User" };
  return (
    <Modal title={titles[type]} onClose={onClose}>
      <div className="space-y-4" data-testid="user-modal">
        {type === "delete" ? (
          <p className="text-sm text-[#A3A3A3]">Remove <span className="text-white font-medium">{user.email}</span>? This cannot be undone.</p>
        ) : type === "reset" ? (
          <ModalField label="New password" testid="user-password-input" type="password" value={f.password} onChange={set("password")} />
        ) : (
          <>
            <ModalField label="Name" testid="user-name-input" value={f.name} onChange={set("name")} />
            {type === "add" && <>
              <ModalField label="Email" testid="user-email-input" type="email" value={f.email} onChange={set("email")} />
              <ModalField label="Password" testid="user-password-input" type="password" value={f.password} onChange={set("password")} />
            </>}
            <ModalSelect label="Role" testid="user-role-select" value={f.role} onChange={set("role")}>
              <option value="admin">Admin</option>
              <option value="operator">Operator</option>
              <option value="viewer">Viewer</option>
              {type === "edit" && user?.role === "owner" && <option value="owner">Owner</option>}
            </ModalSelect>
          </>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-sm text-[#A3A3A3] hover:text-white px-4 py-2">Cancel</button>
          <button data-testid="submit-user-modal" disabled={loading} onClick={act}
            className={`text-white text-sm font-medium px-4 py-2 rounded-sm disabled:opacity-60 transition-colors duration-150 ${type === "delete" ? "bg-red-600 hover:bg-red-500" : "bg-[#3A86FF] hover:bg-[#2563EB]"}`}>
            {loading ? "Working…" : type === "delete" ? "Remove" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
